import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import useAuth from '../../hooks/useAuth'
import { db, isAbortError } from '../../lib/supabase'
import { getLocalDraft, clearLocalDraft } from '../../hooks/useSurveyForm'
import SurveyForm from '../../components/SurveyForm'

export default function AuthenticatedSurvey() {
  const { t } = useTranslation(['common', 'survey'])
  const { user } = useAuth()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [surveyOptions, setSurveyOptions] = useState(null)

  useEffect(() => {
    async function loadClientData() {
      try {
        // Check if already completed
        const respondent = await db.getMyRespondent(user.id)
        if (respondent?.completed_at) {
          // Clean up any orphaned drafts
          try { await db.deleteDraft(user.id) } catch { /* ignore */ }
          clearLocalDraft(user.id)
          navigate('/client', { replace: true })
          return
        }

        // Get client pair data for pre-filling
        const pair = await db.getMyClientPair(user.id)
        if (!pair) {
          setError('No client pair found for your account.')
          setLoading(false)
          return
        }

        // Determine if this user is male or female client
        const isMale = pair.male_client_id === user.id
        const clientName = isMale ? pair.male_name : pair.female_name
        const clientEmail = isMale ? pair.male_email : pair.female_email
        const partnerName = isMale ? pair.female_name : pair.male_name
        const partnerEmail = isMale ? pair.female_email : pair.male_email
        const sexOptions = t('survey:profile.q3.options', { returnObjects: true })
        const biologicalSex = isMale ? sexOptions[0] : sexOptions[1]

        // Pair fields always override (read-only)
        const pairValues = {
          q1: clientName,
          q2: clientEmail,
          q3: biologicalSex,
          q5: partnerName,
          q6: partnerEmail,
        }

        const readOnlyFields = {
          q1: true,
          q2: true,
          q3: true,
          q5: true,
          q6: true,
        }

        // Load drafts from both sources
        const [serverDraft, localDraft] = await Promise.all([
          db.getDraft(user.id),
          Promise.resolve(getLocalDraft(user.id)),
        ])

        // Pick the most recent draft using timestamps
        let initialValues = pairValues
        let initialStep = 0

        if (serverDraft || localDraft) {
          const serverTs = serverDraft ? new Date(serverDraft.updated_at).getTime() : 0
          const localTs = localDraft?.ts || 0

          if (localTs > serverTs && localDraft) {
            // localStorage is newer (same device, has current page data)
            initialValues = { ...localDraft.formValues, ...pairValues }
            initialStep = localDraft.currentStep
          } else if (serverDraft) {
            // Server draft is newer (different device continued, or no localStorage)
            initialValues = { ...serverDraft.form_data, ...pairValues }
            initialStep = serverDraft.current_step
          }
        }

        // Clear localStorage after loading (fresh session tracking starts)
        clearLocalDraft(user.id)

        setSurveyOptions({
          userId: user.id,
          clientPairId: pair.id,
          initialValues,
          initialStep,
          readOnlyFields,
        })
        setLoading(false)
      } catch (err) {
        if (isAbortError(err)) return
        console.error('Error loading client data:', err)
        setError(err.message)
        setLoading(false)
      }
    }

    loadClientData()
  }, [user.id, navigate, t])

  if (loading) {
    return <div className="text-center py-12 text-gray-500">{t('common:status.loading')}</div>
  }

  if (error) {
    return (
      <div className="max-w-xl mx-auto px-5 py-12 text-center">
        <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-xl">
          {error}
        </div>
      </div>
    )
  }

  return (
    <SurveyForm
      options={surveyOptions}
      onCompleted={() => navigate('/client')}
    />
  )
}
